import { useModal } from 'vue-final-modal';
import ConfirmCloseModal from '@/shared/ui/modals/ConfirmCloseModal.vue';
import {
  CERTIFICATE_OVERWRITE_DESCRIPTION,
  CERTIFICATE_OVERWRITE_TITLE,
} from '../lib/certificateOverwriteMessages';
import CertificateUploadModal from '../ui/view/CertificateUploadModal.vue';

export type CertificateUploadStatus = 'idle' | 'uploading' | 'completed';

export interface CertificateUploadTarget {
  memberTrainingId: number;
  hasCertificate: boolean;
}

/**
 * Os dois modais são criados uma única vez, no setup de quem chama, e recebem o alvo de cada
 * clique por `patchOptions`. Criar um `useModal` por clique deixaria uma instância dinâmica órfã
 * a cada certificado enviado, porque a limpeza da vue-final-modal depende do unmount do dono.
 */
export function useCertificateUpload(onUploaded: () => void) {
  let pendingTrainingId: number | null = null;
  let pendingMemberTrainingId: number | null = null;

  const uploadModal = useModal({
    component: CertificateUploadModal,
    attrs: {
      trainingId: 0,
      memberTrainingId: 0,
      onClose: () => {
        uploadModal.close();
      },
      onUploaded: () => {
        onUploaded();
      },
    },
  });

  const overwriteModal = useModal({
    component: ConfirmCloseModal,
    attrs: {
      clickToClose: false,
      escToClose: false,
      title: CERTIFICATE_OVERWRITE_TITLE,
      description: CERTIFICATE_OVERWRITE_DESCRIPTION,
      onClose: () => {
        overwriteModal.close();
      },
      onConfirm: () => {
        overwriteModal.close();
        openUploadModal();
      },
    },
  });

  function openUploadModal(): void {
    if (pendingTrainingId === null || pendingMemberTrainingId === null) return;

    uploadModal.patchOptions({
      attrs: {
        trainingId: pendingTrainingId,
        memberTrainingId: pendingMemberTrainingId,
      },
    });
    uploadModal.open();
  }

  function openCertificateUpload(trainingId: number, target: CertificateUploadTarget): void {
    pendingTrainingId = trainingId;
    pendingMemberTrainingId = target.memberTrainingId;

    if (target.hasCertificate) {
      overwriteModal.open();
      return;
    }

    openUploadModal();
  }

  return { openCertificateUpload };
}

export default useCertificateUpload;
